import { defineStore } from 'pinia';
import { streamEmailSettings, setEmailSettings } from '@/services/messages.js';

export const useEmailSettingsStore = defineStore('emailSettings', {
  state: () => ({
    firmId: null,
    userId: null,
    emailSettings: {},
    settingsUnsubscribe: null,
    processing_status: 'init',
    timeframe: null,
    counter: 0,
    // background sync/processing status (drives the status pill UI)
    syncStatus: 'idle', // 'idle' | 'syncing' | 'error'
    syncedThisRun: 0,
    syncError: null,
    unprocessedCount: 0,
    syncNonce: 0, // bumped to wake the sync loop early (e.g. horizon changed)
  }),
  getters: {
    settings: (state) => state.emailSettings,
    ignoredSenders: (state) => (state.emailSettings && state.emailSettings.ignoredSenders) ? state.emailSettings.ignoredSenders : [],
    newestProcessedTimestamp: (state) => state.emailSettings ? state.emailSettings.newestProcessedTimestamp: null,
    oldestProcessedTimestamp: (state) => state.emailSettings ? state.emailSettings.oldestProcessedTimestamp: null,
    processingPaused: (state) => !!(state.emailSettings && state.emailSettings.processingPaused),
  },
  actions: {
    updateSettings(settings) {
      console.log('updateSettings', settings);
      return setEmailSettings(this.firmId, this.userId, settings);
    },
    updateIgnoreSenders(ignored_senders) {
      const fsIgnoredSenders = this.ignoredSenders;
      // add the new ignored senders to the list
      for (let i=0; i<ignored_senders.length; i++) {
        if (!fsIgnoredSenders.includes(ignored_senders[i])) {
          fsIgnoredSenders.push(ignored_senders[i]);
        }
      }
      return this.updateSettings({ignoredSenders: fsIgnoredSenders});
    },
    subscribeToSettings(firmId, userId) {
      if (!firmId || !userId) {
        throw new Error('firm and user required');
      }
      if (firmId != this.firmId || userId != this.userId) {
        this.firmId = firmId;
        this.userId = userId;
        if (this.settingsUnsubscribe && typeof this.settingsUnsubscribe === 'function') {
          this.settingsUnsubscribe();
        }
        console.log('subscribing to email settings', this.firmId, this.userId);
        this.settingsUnsubscribe = streamEmailSettings(this.firmId, this.userId, (snapshot) => {
          this.emailSettings = snapshot.data()['emailSettings']?? {};
          // restore the persisted sync horizon (timeframe) across sessions
          if (this.emailSettings.timeframe) {
            this.timeframe = this.emailSettings.timeframe;
          }
        });
      }
    },
    startProcessing() {
      this.processing_status = 'started';
    },
    stopProcessing() {
      this.processing_status = 'stopped';
    },
    setTimeframe(timeframe) {
      console.log('setTimeframe', timeframe);
      this.timeframe = timeframe;
      // persist as the sync horizon setting, and wake the sync loop so a
      // widened horizon backfills promptly
      this.updateSettings({timeframe: timeframe});
      this.syncNonce++;
    },
    increaseCounter() {
      this.counter++;
    },
    resetCounter() {
      this.counter = 0;
    },
    setSyncStatus(status, syncedThisRun = null, error = null) {
      this.syncStatus = status;
      if (syncedThisRun !== null) {
        this.syncedThisRun = syncedThisRun;
      }
      this.syncError = error;
    },
    setUnprocessedCount(n) {
      this.unprocessedCount = n;
    },
    // Pause/resume only gates processing (matching -> Firestore writes);
    // background sync keeps running so the local cache stays fresh.
    pauseProcessing() {
      return this.updateSettings({processingPaused: true});
    },
    resumeProcessing() {
      const p = this.updateSettings({processingPaused: false});
      this.syncNonce++; // wake the loop to drain the backlog now
      return p;
    },
  },
});

